import { useState, useEffect } from 'react'
import classNames from 'classnames'
import { IconContext } from 'react-icons'
import { AiOutlineSearch } from 'react-icons/ai'
import { useRouter } from 'next/router'
import Link from 'next/link'
import Highlighter from 'react-highlight-words'
import Loader from '@/components/common/Loader'
import TextInput from '@/components/common/TextInput'
import { useDispatch } from 'react-redux'
import { setFilters } from '@/store/slices/speakersSlice'
import useDebounce from '@/hooks/useDebounce'
import { request } from '@/utils/axiosUtil'

const MobileSearch = ({ close }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const [searchText, setSearchText] = useState('')
  const [viewText, setViewText] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [show, setShow] = useState(false)
  const [speakersDetails, setSpeakersDetails] = useState(['Loading...'])
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useDebounce(
    async () => {
      if (searchText?.length >= 2) {
        setSpeakersDetails(['Loading...'])
        setIsLoading(true)
        const apiUrl = `/unifiedAutoPopulate?query=${encodeURIComponent(
          searchText?.trim()?.toLowerCase()
        )}`
        const res = await request({ url: apiUrl })
        const result = res?.status == 200 ? res?.data?.data : res?.response?.data
        setSpeakersDetails(result || [])
        setIsLoading(false)
      }
    },
    8e2,
    [searchText]
  )

  useEffect(() => {
    if (
      searchText == '' ||
      suggestions.length == 0 ||
      (suggestions?.nameList?.length == 0 &&
        suggestions?.categoryList?.length == 0 &&
        suggestions?.speakerTypeList.length == 0)
    ) {
      setShow(false)
      return
    }
    setShow(true)
  }, [
    searchText,
    suggestions,
    suggestions?.nameList,
    suggestions?.categoryList,
    suggestions?.speakerTypeList,
  ])

  useEffect(() => {
    let tempSuggestions = { nameList: [], categoryList: [], speakerTypeList: [] }
    let tempShow = !searchText

    if (validateInput(searchText)) {
      if (speakersDetails?.length > 0) {
        tempSuggestions = getSuggestions(speakersDetails)
        tempShow = !(
          tempSuggestions?.nameList?.length === 0 &&
          tempSuggestions?.categoryList?.length === 0 &&
          tempSuggestions?.speakerTypeList.length === 0
        )
      }
    }
    setShow(tempShow)
    setSuggestions(tempSuggestions)
  }, [searchText, speakersDetails])

  const MouseOver = (category) => {
    if (searchText.length >= 2) {
      setViewText(category)
      return
    }
    setViewText('')
  }

  const MouseOut = () => {
    setViewText('')
  }

  const onInputChange = (e) => {
    const searchString = e.target.value
    if (searchString.length >= 2) {
      setSpeakersDetails(['Loading...'])
    }
    setSearchText(e.target.value)
    validateInput(e.target.value)
  }

  const getSuggestions = (speakers) => {
    const result = {
      nameList: [],
      categoryList: [],
      speakerTypeList: [],
    }

    speakers.forEach((item = {}) => {
      const { id = null, contentType = null, name = null, slug = null, assets = {} } = item
      switch (contentType) {
        case 'speaker':
          result.nameList.push({
            name,
            slug,
            image_url: assets?.fields?.file?.url,
          })
          break
        case 'speakerTopic':
          result.categoryList.push({
            id,
            slug,
            category: name,
          })
          break
        case 'speakerTypes':
          result.speakerTypeList.push({
            id,
            slug,
            type: name,
          })
          break
        default:
          break
      }
    })
    return result
  }
  const validateInput = (text) => {
    if (!text || text.length < 2) {
      setError('Enter at least 2 characters to search')
      return false
    } else {
      error && setError('')
      return true
    }
  }

  const onSearchRedirect = () => {
    setViewText('')
    if (validateInput(searchText)) {
      dispatch(
        setFilters({
          categories: null,
          searchQuery: '',
          types: null,
          profileCategory: null,
          relatedSpeakers: null,
          sortOrder: null,
        })
      )
      router.push({
        pathname: '/speakers',
        query: { query: searchText.trim() },
      })
      return
    }
    setViewText('')
  }

  const onCategoryClick = (topic) => {
    router.push({
      pathname: `/speakers/topics/${topic?.slug}`,
    })
  }
  const onTypeClick = (speakertype) => {
    router.push({
      pathname: `/speakers/types/${speakertype?.slug}`,
    })
  }

  return (
    <div>
      <div
        className={classNames(
          ' z-[99] inset-x-1/2 bg-black  md:mt-6 bg-no-repeat bg-bottom bg-contain'
        )}
      >
        <div className="relative block bg-black cursor-pointer md:hidden">
          <TextInput
            className="w-full h-12 pl-4 pr-16 bg-black border-b rounded-sm border-custom-gray-80 placeholder:text-sm placeholder:text-white text-white placeholder:font-thin focus:outline-none"
            placeHolder="Search by Speaker Name, Topic, or Type"
            value={viewText ? viewText : searchText}
            onKeyPress={(event) => {
              if (event.key === 'Enter') {
                onSearchRedirect()
              }
            }}
            onChange={onInputChange}
          />

          <span className="absolute inset-y-0 right-0 flex items-center pr-4">
            <IconContext.Provider
              value={{
                size: '1.8em',
                color: '#ffffff',
              }}
            >
              <AiOutlineSearch onClick={() => onSearchRedirect()} />
            </IconContext.Provider>
          </span>
        </div>
        <div
          className={`${show ? 'relative m-auto px-4' : 'relative m-auto md:mb-14 mt-2.5  px-4'}`}
        >
          <span className="text-sm font-normal leading-7 regular-font">
            {error == '' ? '' : error}
          </span>
          {speakersDetails?.length === 0 &&
            speakersDetails[0] !== 'Loading...' &&
            searchText.length >= 2 && (
              <span className="text-lg regular-font font-normal leading-7">
                No speakers match your search criteria
              </span>
            )}
          {speakersDetails[0] == 'Loading...' && searchText.length >= 2 && (
            <span className="text-lg font-normal leading-7 regular-font">
              {speakersDetails && speakersDetails}
            </span>
          )}
        </div>
        <div className="relative mt-4">
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <div className="w-full text-white">
                {suggestions?.speakerTypeList &&
                  suggestions?.speakerTypeList?.slice(0, 4).map((item, i) => (
                    <div key={i} className="flex md:gap-6" aria-label="speakerType">
                      <div className="w-full text-xl font-thin leading-loose bg-black hover:bg-opacity-20 hover:bg-gray-200">
                        <a
                          className="cursor-pointer"
                          onClick={() => {
                            dispatch(
                              setFilters({
                                categories: null,
                                searchQuery: '',
                                types: [item?.id],
                                profileCategory: null,
                                relatedSpeakers: null,
                                sortOrder: null,
                              })
                            ),
                              close(),
                              setSearchText(''),
                              setViewText(''),
                              onTypeClick(item)
                          }}
                          onMouseOver={() => MouseOver(item && item.type)}
                          onMouseOut={MouseOut}
                        >
                          <Highlighter
                            className="mt-3 ml-4 text-xl text-gray-400 search-font"
                            highlightClassName="relative text-white bg-inherit opacity-95 bg-opacity-20 font-normal text-xl"
                            searchWords={[searchText]}
                            autoEscape={true}
                            textToHighlight={item && item.type}
                          />
                        </a>
                      </div>
                    </div>
                  ))}

                {suggestions?.categoryList &&
                  suggestions?.categoryList?.slice(0, 4).map((item, i) => (
                    <div key={i} className="flex md:gap-6" aria-label="Category">
                      <div className="w-full text-xl font-thin leading-loose bg-black hover:bg-opacity-20 hover:bg-gray-200 ">
                        <a
                          className="cursor-pointer"
                          onClick={() => {
                            dispatch(
                              setFilters({
                                categories: [item?.id],
                                searchQuery: '',
                                types: null,
                                profileCategory: null,
                                relatedSpeakers: null,
                                sortOrder: null,
                              })
                            ),
                              close(),
                              setSearchText(''),
                              setViewText(''),
                              onCategoryClick(item)
                          }}
                          onMouseOver={() => MouseOver(item && item.category)}
                          onMouseOut={MouseOut}
                        >
                          <Highlighter
                            className="mt-3 ml-4 text-xl text-gray-400 search-font"
                            highlightClassName="relative text-white bg-inherit opacity-95 bg-opacity-20 font-normal text-xl"
                            searchWords={[searchText]}
                            autoEscape={true}
                            textToHighlight={item && item.category}
                          />
                        </a>
                      </div>
                    </div>
                  ))}

                {suggestions?.nameList &&
                  suggestions?.nameList?.slice(0, 4).map((item, i) =>
                    item && item.image_url ? (
                      <div key={i} className="z-0 flex md:gap-6" aria-label="speakerName">
                        <div className="w-full bg-black cursor-pointer hover:bg-opacity-20 hover:bg-gray-200">
                          <Link
                            href={`/speaker/${item?.slug}`}
                            className="cursor-pointer"
                            legacyBehavior
                          >
                            <a
                              onClick={() => {
                                close(), setSearchText(''), setViewText('')
                              }}
                              onMouseOver={() => MouseOver(item?.name)}
                              onMouseOut={MouseOut}
                            >
                              <div className="flex my-2">
                                <img
                                  height={50}
                                  width={50}
                                  objectFit="cover"
                                  objectPosition="top"
                                  src={
                                    item &&
                                    'https:' +
                                      item?.image_url +
                                      '?w=500&h=500&q=100&fit=thumb&f=top'
                                  }
                                  alt="speaker"
                                ></img>
                                <Highlighter
                                  className="mt-4 ml-4 text-xl font-light text-custom-gray-80 search-font"
                                  highlightClassName="relative text-white bg-inherit font-normal text-xl"
                                  searchWords={[searchText]}
                                  autoEscape={true}
                                  textToHighlight={item?.name}
                                />
                              </div>
                            </a>
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div key={i}></div>
                    )
                  )}
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  )
}

export default MobileSearch
